import React, { useState, useRef, useEffect } from 'react';
import { FiBookOpen, FiChevronDown, FiMenu, FiX } from 'react-icons/fi';

/** Read the language Google Translate stored in its cookie (/en/sv → 'sv') */
function getActiveGoogleLang(): string {
  try {
    const match = document.cookie.match(/googtrans=\/[a-z-]+\/([a-z-]+)/);
    if (match && match[1] && match[1] !== 'en') return match[1];
  } catch {}
  return localStorage.getItem('siteLang') || 'en';
}

export default function Header() {
  const [lang, setLang] = useState<string>(() => getActiveGoogleLang());
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const langs = [
    { code: 'en', flag: '🇬🇧', display: 'English' },
    { code: 'sv', flag: '🇸🇪', display: 'Svenska' },
    { code: 'es', flag: '🇪🇸', display: 'Español' },
    { code: 'de', flag: '🇩🇪', display: 'Deutsch' },
  ];

  const activeLang = langs.find(l => l.code === lang) ?? langs[0];

  const changeLanguage = (code: string) => {
    setLang(code);
    setOpen(false);
    localStorage.setItem('siteLang', code);
    const domain = window.location.hostname;
    if (code === 'en') {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      localStorage.removeItem('siteLang');
    } else {
      document.cookie = `googtrans=/en/${code}; path=/`;
      document.cookie = `googtrans=/en/${code}; path=/; domain=${domain}`;
    }
    setTimeout(() => {
      const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
      if (combo) {
        combo.value = code;
        combo.dispatchEvent(new Event('change'));
      }
    }, 150);
  };

  const navigateTo = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    e.preventDefault();
    if (window.location.pathname === to) return;
    window.history.pushState({}, '', to);
    window.dispatchEvent(new Event('app:navigate'));
    setMobileOpen(false);
  };

  const navigateToContact = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setMobileOpen(false);
    setOpen(false);

    const scrollToContact = () => {
      const section = document.getElementById('contact-form');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/#contact-form');
      window.dispatchEvent(new Event('app:navigate'));
      setTimeout(scrollToContact, 120);
      return;
    }

    window.history.replaceState({}, '', '/#contact-form');
    scrollToContact();
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-[#f2d8cc] shadow-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">

        {/* ── Logo ── */}
        <a
          href="/"
          onClick={(e) => navigateTo(e, '/')}
          className="flex items-center gap-2.5 shrink-0"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--brand-accent)] text-white shadow-sm">
            <FiBookOpen className="h-5 w-5" />
          </span>
          <span className="text-base font-bold tracking-tight text-[var(--brand-ink)] sm:text-lg">
            Digital Book Shelf
          </span>
        </a>

        {/* ── Desktop Nav (centered) ── */}
        <nav className="hidden absolute left-1/2 -translate-x-1/2 items-center gap-1 md:flex">
          {[
            { label: 'Home', to: '/' },
            { label: 'FAQs', to: '/faqs' },
          ].map(({ label, to }) => (
            <a
              key={to}
              href={to}
              onClick={(e) => navigateTo(e, to)}
              className="rounded-lg px-4 py-2 text-sm font-medium text-[#4c5f75] transition-colors hover:bg-[#fff4ec] hover:text-[var(--brand-ink)]"
            >
              {label}
            </a>
          ))}
        </nav>

        {/* ── Right side: language + contact ── */}
        <div className="flex items-center gap-2 shrink-0">

          {/* Language picker – desktop */}
          <div className="relative hidden sm:block" ref={menuRef}>
            <button
              onClick={() => setOpen(v => !v)}
              aria-haspopup="true"
              aria-expanded={open}
              translate="no"
              className="inline-flex items-center gap-2 rounded-lg border border-[#e8d5cb] bg-white px-3 py-2 text-sm font-medium text-[var(--brand-ink)] transition-all hover:border-[var(--brand-accent)]/60 hover:bg-[#fff4ec]"
            >
              <span className="text-base leading-none">{activeLang.flag}</span>
              <span>{activeLang.display}</span>
              <FiChevronDown
                className={`h-3.5 w-3.5 text-[var(--text-muted)] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              />
            </button>

            {open && (
              <div className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-xl border border-[#f2d8cc] bg-white shadow-lg">
                {langs.map(l => (
                  <button
                    key={l.code}
                    onClick={() => changeLanguage(l.code)}
                    translate="no"
                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                      lang === l.code
                        ? 'bg-[var(--brand-accent-soft)] font-semibold text-[var(--brand-ink)]'
                        : 'text-[var(--text-muted)] hover:bg-[#fff4ec]'
                    }`}
                  >
                    <span className="text-base">{l.flag}</span>
                    {l.display}
                  </button>
                ))}
              </div>
            )}
          </div>


      

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(v => !v)}
            className="rounded-lg border border-[#f2d8cc] bg-white p-2 text-[var(--text-muted)] transition-colors hover:text-[var(--brand-accent)] md:hidden"
          >
            {mobileOpen ? <FiX className="h-5 w-5" /> : <FiMenu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* ── Mobile menu ── */}
      {mobileOpen && (
        <div className="border-t border-[#f2d8cc] bg-white px-5 py-4 md:hidden">
          <nav className="space-y-1">
            {[
              { label: 'Home', to: '/' },
              { label: 'FAQs', to: '/faqs' },
            ].map(({ label, to }) => (
              <a
                key={to}
                href={to}
                onClick={(e) => navigateTo(e, to)}
                className="block rounded-lg px-3 py-2.5 text-sm font-medium text-[#102a43] hover:bg-[#fff4ec]"
              >
                {label}
              </a>
            ))}
          </nav>

          <div className="mt-4 border-t border-[#f2d8cc] pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Language</p>
            <div className="flex flex-wrap gap-2">
              {langs.map(l => (
                <button
                  key={l.code}
                  onClick={() => { changeLanguage(l.code); setMobileOpen(false); }}
                  translate="no"
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    lang === l.code
                      ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)] text-white'
                      : 'border-[#e8d5cb] text-[var(--brand-ink)] hover:border-[var(--brand-accent)]/60'
                  }`}
                >
                  <span className="text-sm">{l.flag}</span>
                  {l.display}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
